/**
 * jobCosting.js — Field Canopy canonical job costing module
 *
 * Covers: labor hours, labor cost, job costs (material/other), total costs,
 * gross profit, gross margin, and planned vs actual row derivation.
 *
 * Scope boundary:
 *   - This module handles labor + job_costs only.
 *   - Payment, invoice, and effective contract logic lives in jobFinancials.js.
 *   - Do NOT import from jobFinancials.js here — the two modules are independent.
 *   - actualRevenue / totalContract is always passed in from the caller (Jobs.js),
 *     which gets it from deriveJobFinancials(). This module never independently
 *     computes effective contract or change order totals.
 *
 * All functions are pure: same inputs → same outputs, no side effects, no DB calls.
 *
 * See Docs/DECISIONS_LOG.md FA-010 for canonical module scope decision.
 */

// ─── Labor ─────────────────────────────────────────────────────────────────

/**
 * Derive labor hours and labor cost from time_entries.
 *
 * Only counts closed entries (clock_out is not null). In-progress entries are
 * excluded — they represent uncommitted time. This matches the existing behavior
 * in Jobs.js and must not be changed without explicit approval.
 *
 * Labor rate: t.employee?.hourly_rate || 0
 * A null or missing hourly_rate silently contributes $0 labor cost (not an error).
 * This preserves the existing behavior and avoids crashes on unlinked profiles.
 *
 * @param {Array} timeEntries — supabase rows with clock_in, clock_out, employee: { hourly_rate }
 * @returns {{ totalHours: number, totalLaborCost: number }}
 */
export function deriveLaborMetrics(timeEntries) {
  let totalHours = 0
  let totalLaborCost = 0

  for (const t of timeEntries) {
    if (!t.clock_out) continue
    const hrs = (new Date(t.clock_out) - new Date(t.clock_in)) / 3600000
    totalHours += hrs
    totalLaborCost += hrs * (t.employee?.hourly_rate || 0)
  }

  return { totalHours, totalLaborCost }
}

// ─── Job Costs ─────────────────────────────────────────────────────────────

/**
 * Derive cost totals from job_costs rows.
 *
 * Materials are identified by category === 'materials'.
 * All other categories (equipment, subcontractor, dump_fee, fuel, rental, other)
 * contribute to totalOtherCost. totalJobCosts = all categories combined.
 *
 * @param {Array} costs — supabase rows with category (string) and amount (number|null)
 * @returns {{
 *   totalMaterialCost: number,
 *   totalOtherCost: number,
 *   totalJobCosts: number
 * }}
 */
export function deriveCostMetrics(costs) {
  let totalMaterialCost = 0
  let totalOtherCost = 0

  for (const c of costs) {
    const amount = c.amount || 0
    if (c.category === 'materials') {
      totalMaterialCost += amount
    } else {
      totalOtherCost += amount
    }
  }

  return {
    totalMaterialCost,
    totalOtherCost,
    totalJobCosts: totalMaterialCost + totalOtherCost,
  }
}

// ─── Profitability ──────────────────────────────────────────────────────────

/**
 * Derive profitability from actual revenue (contract) and cost totals.
 *
 * actualRevenue is the caller's totalContract — passed from Jobs.js, which gets
 * it from deriveJobFinancials() in jobFinancials.js. This module never independently
 * computes effective contract or change order totals.
 *
 * Margin is 0 when actualRevenue is 0 or negative (divide-by-zero guard).
 * This matches the existing inline behavior in Jobs.js.
 *
 * @param {number} actualRevenue  — totalContract from Jobs.js (effectiveContract + tax)
 * @param {number} totalLaborCost — from deriveLaborMetrics
 * @param {number} totalJobCosts  — from deriveCostMetrics
 * @returns {{
 *   totalAllCosts: number,
 *   profit: number,
 *   margin: number,
 *   hasContract: boolean
 * }}
 */
export function deriveProfitability(actualRevenue, totalLaborCost, totalJobCosts) {
  const totalAllCosts = totalJobCosts + totalLaborCost
  const profit = actualRevenue - totalAllCosts
  const margin = actualRevenue > 0 ? (profit / actualRevenue * 100) : 0
  const hasContract = actualRevenue > 0

  return { totalAllCosts, profit, margin, hasContract }
}

// ─── Combined convenience ───────────────────────────────────────────────────

/**
 * Derive all job costing metrics in one call.
 *
 * This is the primary function callers should use. It runs deriveLaborMetrics,
 * deriveCostMetrics, and deriveProfitability internally and returns a flat result.
 *
 * actualRevenue must come from the caller — it is the same totalContract value
 * already computed via deriveJobFinancials() in jobFinancials.js. Do not pass
 * job.contract_amount directly; pass the already-derived contract value.
 *
 * @param {Array}  timeEntries  — supabase rows: clock_in, clock_out, employee: { hourly_rate }
 * @param {Array}  costs        — supabase rows: category, amount
 * @param {number} actualRevenue — totalContract from Jobs.js
 * @returns {{
 *   totalHours: number,
 *   totalLaborCost: number,
 *   totalMaterialCost: number,
 *   totalOtherCost: number,
 *   totalJobCosts: number,
 *   totalAllCosts: number,
 *   profit: number,
 *   margin: number,
 *   hasContract: boolean
 * }}
 */
export function deriveJobCosting(timeEntries, costs, actualRevenue) {
  const { totalHours, totalLaborCost } = deriveLaborMetrics(timeEntries)
  const { totalMaterialCost, totalOtherCost, totalJobCosts } = deriveCostMetrics(costs)
  const { totalAllCosts, profit, margin, hasContract } = deriveProfitability(actualRevenue, totalLaborCost, totalJobCosts)

  return {
    totalHours,
    totalLaborCost,
    totalMaterialCost,
    totalOtherCost,
    totalJobCosts,
    totalAllCosts,
    profit,
    margin,
    hasContract,
  }
}

// ─── Planned vs Actual ──────────────────────────────────────────────────────

/**
 * Produce the Plan vs Actual row data for the PvA tab.
 *
 * Returns structured data only — no rendering, no JSX, no formatting applied.
 * Callers use the `format` field to format values at render time:
 *   '$' → currency   'h' → hours   '%' → percentage
 *
 * `better` controls variance coloring:
 *   'high' → higher actual is better (revenue, profit, margin)
 *   'low'  → lower actual is better (costs, hours)
 *
 * planned_* values come from job columns frozen at job creation from the estimate.
 * actualRevenue is the same totalContract passed to deriveJobCosting.
 *
 * @param {object} job           — jobs row; must have planned_* columns
 * @param {object} costingResult — return value of deriveJobCosting
 * @param {number} actualRevenue — totalContract from Jobs.js (same value as passed to deriveJobCosting)
 * @returns {Array<{
 *   label: string,
 *   planned: number|null,
 *   actual: number,
 *   format: '$'|'h'|'%',
 *   better: 'high'|'low'
 * }>}
 */
/**
 * Derive summary-level variances from PvA rows for the summary header.
 * Returns null per metric when planned value is missing (null or ≤ 0).
 *
 * @param {Array} rows — return value of derivePvaRows
 * @returns {{ revenue, cost, profit, margin }}
 *   Each value: null (no plan) or { diff: number, pct: string, good: boolean }
 */
export function derivePvaSummary(rows) {
  const byLabel = Object.fromEntries(rows.map(r => [r.label, r]))

  const variance = (label) => {
    const r = byLabel[label]
    if (!r || !(r.planned > 0)) return null
    const diff = r.actual - r.planned
    const pct  = (Math.abs(diff) / r.planned * 100).toFixed(1)
    const good = r.better === 'high' ? diff >= 0 : diff <= 0
    return { diff, pct, good }
  }

  return {
    revenue: variance('Revenue'),
    cost:    variance('Total Cost'),
    profit:  variance('Gross Profit'),
    margin:  variance('Margin'),
  }
}

export function derivePvaRows(job, costingResult, actualRevenue) {
  const {
    totalHours,
    totalLaborCost,
    totalMaterialCost,
    totalAllCosts,
    profit,
    margin,
  } = costingResult

  return [
    { label: 'Revenue',       planned: job.planned_revenue,       actual: actualRevenue,   format: '$', better: 'high' },
    { label: 'Material Cost', planned: job.planned_material_cost, actual: totalMaterialCost, format: '$', better: 'low'  },
    { label: 'Labor Cost',    planned: job.planned_labor_cost,    actual: totalLaborCost,  format: '$', better: 'low'  },
    { label: 'Labor Hours',   planned: job.planned_labor_hours,   actual: totalHours,      format: 'h', better: 'low'  },
    { label: 'Total Cost',    planned: job.planned_total_cost,    actual: totalAllCosts,   format: '$', better: 'low'  },
    { label: 'Gross Profit',  planned: job.planned_gross_profit,  actual: profit,          format: '$', better: 'high' },
    { label: 'Margin',        planned: job.planned_margin,        actual: margin,          format: '%', better: 'high' },
  ]
}
